/**
 * Single source of truth for partner-side RBAC checks.
 *
 * Why this file exists
 * --------------------
 * Pre-existing code applied permission filtering in TWO places:
 *   1. PartnerShell.jsx — sidebar visibility (worked correctly)
 *   2. App.js Protected — route guard (only checked ut === kind)
 *
 * The mismatch meant a `partner_team` (agent) user who knew a URL
 * could just type `/partner/success` and bypass the sidebar's filter.
 * That's the vulnerability the merchant reported on 26 Jun 2026.
 *
 * Now both surfaces import from here, so adding a new page requires
 * EXACTLY ONE change (the NAV table in PartnerShell.jsx) and the
 * route guard inherits the same rules automatically via PageGuard.
 */

/**
 * Decide whether `user` may access a NAV item or route with these props.
 *
 * Args
 * ----
 * user         : the AuthProvider's user object (may be null/undefined
 *                during the loading phase — caller should gate on
 *                loading separately).
 * item         : { perm?, adminOnly?, always?, gated? } — same shape
 *                used by the NAV table.
 *
 * Returns true if the user may see the page, false otherwise.
 */
export function canAccessPage(user, item = {}) {
    if (!user) return false;
    // Partner-level feature flag (e.g. AA must be enabled for the org).
    // Hard requirement independent of role.
    if (item.gated && !user?.partner?.[item.gated]) return false;
    // Profile, support, etc.
    if (item.always) return true;
    // Admin-side users (ut === "admin") pass straight through — this
    // module is partner-side only. Admin role checks live in
    // backend/core/auth.py (require_admin).
    if (user.ut === "admin") return true;
    // Partner owners get every non-gated page.
    if (user.role === "partner_admin") return true;
    // Below this point: partner_team (agent).
    if (item.adminOnly) return false;
    // Default-deny for items without a permission key — protects
    // against accidentally exposing a new page by forgetting to set
    // `perm` in the NAV table.
    if (!item.perm) return false;
    const v = (user.permissions || {})[item.perm];
    return v === "read" || v === "write";
}
